// src/services/mysql.js — Frontend MySQL/MariaDB service
import axios from 'axios';
import { API_URL as BASE_API_URL } from '../config.js';

// Server error code for "no instance picked and no server default" -- the
// panel shows a "select a data source" state rather than a red error.
export const NO_INSTANCE_CODE = 'MYSQL_NO_INSTANCE';

/** The server's MySQL error code from an axios error, if any. */
export function mysqlErrorCode(error) {
  return error?.response?.data?.code || null;
}

// `target` = { instanceId, plantId }: which registered MysqlInstance to use and
// the plant (tenant) the dashboard acts under. The server checks the pair; with
// no instanceId it falls back to its env default connection, if it has one.
function targetParams({ instanceId, plantId } = {}) {
  const out = {};
  if (instanceId) out.instanceId = instanceId;
  if (plantId) out.plantId = plantId;
  return out;
}

class MySQLService {
  getAuthHeaders() {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }

  /**
   * Execute a SQL query against MySQL. Returns array of row objects.
   * Response is normalized server-side to {columns, dataset} — same as QuestDB.
   */
  async query(sql, database, target = {}, _retries = 3) {
    try {
      const response = await axios.get(`${BASE_API_URL}/mysql/query`, {
        params: { sql, database, ...targetParams(target) },
        headers: this.getAuthHeaders()
      });
      return this.formatData(response.data);
    } catch (error) {
      const status = error.response?.status;
      // 503 = server busy (global concurrency), 429 = rate-limit / per-user
      // concurrency. Both are transient back-pressure, not failures — a
      // dashboard with many MySQL panels can briefly exceed the per-user caps.
      // Honor the server's Retry-After (seconds), with a little jitter on 429 so
      // the panels don't all retry on the same tick, and give up after a few
      // tries rather than hang.
      if ((status === 503 || status === 429) && _retries > 0) {
        const hinted = Number(error.response?.headers?.['retry-after']);
        const baseMs = Number.isFinite(hinted) && hinted >= 0 ? hinted * 1000 : 1000;
        const jitterMs = status === 429 ? Math.floor(Math.random() * 250) : 0;
        await new Promise(r => setTimeout(r, baseMs + jitterMs));
        return this.query(sql, database, target, _retries - 1);
      }
      console.error('MySQL query error:', error);
      throw error;
    }
  }

  /** Data sources (registered MySQL instances) this plant may query. */
  async listInstances(plantId) {
    const response = await axios.get(`${BASE_API_URL}/mysql/instances`, {
      params: { plantId },
      headers: this.getAuthHeaders()
    });
    return response.data?.instances || [];
  }

  /** Get available databases */
  async getDatabases(target = {}) {
    try {
      const response = await axios.get(`${BASE_API_URL}/mysql/databases`, {
        params: targetParams(target),
        headers: this.getAuthHeaders()
      });
      return this.formatData(response.data);
    } catch (error) {
      console.error('MySQL databases error:', error);
      throw error;
    }
  }

  /** Get tables in a database */
  async getTables(database, target = {}) {
    try {
      const response = await axios.get(`${BASE_API_URL}/mysql/tables`, {
        params: { database, ...targetParams(target) },
        headers: this.getAuthHeaders()
      });
      return this.formatData(response.data);
    } catch (error) {
      console.error('MySQL tables error:', error);
      throw error;
    }
  }

  /** Column metadata for a table: [{ name, type }] */
  async getColumns(database, table, target = {}) {
    try {
      const response = await axios.get(`${BASE_API_URL}/mysql/columns`, {
        params: { database, table, ...targetParams(target) },
        headers: this.getAuthHeaders()
      });
      return this.formatData(response.data);
    } catch (error) {
      console.error('MySQL columns error:', error);
      throw error;
    }
  }

  /**
   * Format {columns, dataset} response to array of objects.
   * Same logic as questdbService.formatData — kept here to avoid circular deps.
   */
  formatData(response) {
    if (!response || !response.dataset || !response.columns) return [];

    const { columns, dataset } = response;
    if (dataset.length === 0) return [];

    const numericTypes = ['DOUBLE', 'REAL', 'INTEGER', 'BIGINT', 'SMALLINT', 'FLOAT', 'NUMERIC', 'DECIMAL'];

    return dataset.map(row => {
      const obj = {};
      columns.forEach((col, i) => {
        let value = Array.isArray(row) ? row[i] : row[col.name];

        if (col.type === 'TIMESTAMP' && value) {
          const d = new Date(value);
          if (!isNaN(d.getTime())) value = d.toISOString();
        }

        if (numericTypes.includes(col.type?.toUpperCase())) {
          if (typeof value === 'string' && value.trim() !== '') {
            const num = Number(value);
            value = isNaN(num) ? 0 : num;
          } else if (value === null || value === undefined || value === '') {
            value = 0;
          }
        }

        obj[col.name] = value;
      });
      return obj;
    });
  }
}

export default new MySQLService();
